import { useQuery } from "@apollo/client";
import { Typography } from "@lyfegen-platform/react-common";
import { Box, Theme } from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import { startOfMonth } from "date-fns";
import { useState } from "react";
import { Trans } from "react-i18next";
import { GET_RECORD_MATCHED_CASES } from "../../../containers/data/queries";
import { FlexFormStatus } from "../../../types/graphql-global-types";
import { getNthBusinessDayToDate } from "../../../utils/get-nth-business-day";
import FlexTypographyComponent from "../../FlexTypographyComponent";
import LyfeTooltip from "../../LyfeTooltip";
import { getRecordStatusIcon, recordStatusToColor } from "../utils/status";
interface RecordStatusProps {
	props: any;
	children: React.ReactNode;
	palette: Theme["palette"];
	t: any;
}

type TextProp = RecordStatusProps & { hideTooltip: boolean };

function resolveStatus(status: FlexFormStatus): FlexFormStatus {
	return status === FlexFormStatus.COOLING_PERIOD ? FlexFormStatus.UNMATCHED : status;
}

const Text: React.FC<TextProp> = ({ props, palette, t, hideTooltip }) => (
	<Typography
		type="status"
		style={{
			marginLeft: "2px",
			color: (props.status && recordStatusToColor(resolveStatus(props.status), palette, true)) || "black",
		}}
	>
		<FlexTypographyComponent forceHideTooltip={hideTooltip || undefined} type="status" text={t(resolveStatus(props.status))} />
	</Typography>
);

const RecordStatus: React.FC<RecordStatusProps> = ({ props, children, palette, t }) => {
	const [showTooltip, setShowTooltip] = useState(false);
	const { data } = useQuery(GET_RECORD_MATCHED_CASES, { skip: !showTooltip, variables: { id: props.id } });
	const mappedStatus = resolveStatus(props.status);
	const daysToDay6 = getNthBusinessDayToDate(startOfMonth(new Date()), 5).getDate() - new Date().getDate();
	const coolingPeriodMessage =
		daysToDay6 === 1 ? <>{t("cooling-period-message-tomorrow")}</> : <Trans i18nKey="cooling-period-message">{{ daysToDay6 }}</Trans>;
	const matchedCaseCount: number = data?.records[0].matchedCaseCount;
	const matchedStatus = () => (matchedCaseCount > 1 ? t("matched-to-many-cases", { X: matchedCaseCount }) : "");

	const statusIconTooltip = () =>
		props?.status === FlexFormStatus.COOLING_PERIOD ? (
			<LyfeTooltip placement="top" title={coolingPeriodMessage}>
				<InfoOutlined color="disabled" fontSize="small" style={{ marginLeft: 2 }} />
			</LyfeTooltip>
		) : undefined;

	return (
		<LyfeTooltip title={matchedStatus()}>
			<Box
				display="flex"
				alignItems="center"
				style={{
					height: "22px",
					borderRadius: "100px",
					backgroundColor: recordStatusToColor(mappedStatus, palette),
					padding: "3px 8px 3px 6px",
					fontWeight: 600,
					maxWidth: "fit-content",
				}}
				onMouseOver={() => setShowTooltip(true)}
			>
				{getRecordStatusIcon(mappedStatus)}
				<Text hideTooltip={!!matchedStatus()} props={props} palette={palette} t={t} children={children} />
				{statusIconTooltip()}
			</Box>
		</LyfeTooltip>
	);
};

export default RecordStatus;
